import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../ThemeContext';
import { authAPI } from '../helpers/api_service';
import sessionManager from '../helpers/session_manager';
import { handleOAuthLogout, detectOAuthProvider, getOAuthAccessToken, clearAllOAuthData } from '../helpers/oauth_logout';
import { getProfileCompanyPicUrl } from '../helpers/url_helper';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LogoSvg from '../assets/logo.svg';
import './StandaloneTopbar.css';

// Helper function to handle profile picture URL
const getProfilePicUrl = (profilePic) => {
  if (!profilePic) return '';
  
  // Check if URL already starts with http (any protocol)
  if (profilePic.toLowerCase().startsWith('http://') || profilePic.toLowerCase().startsWith('https://')) {
    return profilePic; // Return as is if it's already a full URL
  } else {
    return getProfileCompanyPicUrl(profilePic); // Append profile/company pic URL prefix if it's a relative path
  }
};

function StandaloneTopbar() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [userProfilePic, setUserProfilePic] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Get user data from localStorage
  useEffect(() => {
    const getUserData = () => {
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          setUserEmail(user.email || user.user_email || '');
          setUserFullName(user.full_name || user.user_full_name || '');
          setUserProfilePic(user.profile_pic || user.user_profile_pic || '');
        } catch (err) {
          console.error('Error parsing user data:', err);
        }
      }
    };

    getUserData();

    // Listen for profile updates
    const handleProfileUpdate = (event) => {
      const updatedUserData = event.detail;
      setUserEmail(updatedUserData.email || updatedUserData.user_email || '');
      setUserFullName(updatedUserData.full_name || updatedUserData.user_full_name || '');
      setUserProfilePic(updatedUserData.profile_pic || updatedUserData.user_profile_pic || '');
    };

    window.addEventListener('userProfileUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the options menu
      const optionsMenu = event.target.closest('[data-options-menu]');
      const optionsButton = event.target.closest('[data-options-button]');
      
      if (showOptionsMenu && !optionsMenu && !optionsButton) {
        setShowOptionsMenu(false);
      }
    };

    if (showOptionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu]);

  // Handle logout
  const handleLogout = async () => {
    try {
      // Close the options menu first
      setShowOptionsMenu(false);
      
      // Detect OAuth provider and handle OAuth logout first
      const oauthProvider = detectOAuthProvider();
      if (oauthProvider) {
        const accessToken = getOAuthAccessToken(oauthProvider);
        await handleOAuthLogout(oauthProvider, accessToken);
      }
      
      // Then handle backend logout
      await authAPI.logout();
      
      // Clear session and local storage
      sessionManager.clearSession();
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      clearAllOAuthData();
      
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local data and redirect
      sessionManager.clearSession();
      localStorage.removeItem('userData');
      localStorage.removeItem('userFullName');
      localStorage.removeItem('userProfilePic');
      clearAllOAuthData();
      navigate('/login');
    }
  };

  return (
    <div className="standalone-topbar">
      <div className="standalone-topbar-content">
        {/* Logo Section */}
        <div className="standalone-logo-section">
          <img src={LogoSvg} alt="COMPLIMATE" className="standalone-logo" />
        </div>

        {/* User Profile Section */}
        <div className="standalone-user-profile-section">
          <div 
            className="standalone-user-avatar"
            style={{
              backgroundImage: userProfilePic ? `url(${getProfilePicUrl(userProfilePic)})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              width: '37px',
              height: '37px',
              borderRadius: '50%',
              marginRight: '8px',
              flexShrink: 0,
              backgroundColor: !userProfilePic ? '#009391' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!userProfilePic && (
              <span style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>
                {userFullName ? userFullName.charAt(0).toUpperCase() : 'U'}
              </span>
            )}
          </div>
          <div className="standalone-user-name">{userFullName || 'User'}</div>
          <div className="standalone-user-actions">
            <div style={{ position: 'relative' }}>
              <button 
                className="standalone-header-icon-btn"
                data-options-button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '33px',
                  height: '33px',
                  border: 'none',
                  background: '#009391',
                  borderRadius: '13px',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0
                }}
              >
                <MoreVertIcon style={{ 
                  fontSize: '23px',
                  color: '#fff',
                }} />
              </button>

              {/* Options Menu Dropdown */}
              {showOptionsMenu && (
                <div 
                  data-options-menu
                  style={{
                    position: 'absolute',
                    top: '45px',
                    right: '0px',
                    backgroundColor: mode === 'dark' ? '#333' : '#fff',
                    border: `1px solid ${mode === 'dark' ? '#555' : '#ddd'}`,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    padding: '8px 0',
                    minWidth: '160px',
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      border: 'none',
                      background: 'transparent',
                      color: mode === 'dark' ? '#fff' : '#333',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StandaloneTopbar;
